/**
 * Form Test Mode
 *
 * Development tool that prevents form submissions and displays form data in real-time.
 * Useful for testing forms without actually submitting them to servers or triggering actions.
 *
 * Features:
 * - Prevents all form submissions (preventDefault with capture phase)
 * - Displays live form data as JSON on input/change
 * - Shows formatted submit data with timestamp on submit attempt
 * - Logs browser validation failures
 * - Works with native HTML5 form validation
 *
 * HTML Structure:
 *   <div data-hs-form-test="wrapper">
 *     <form>
 *       <input name="email" required>
 *       <button type="submit">Submit</button>
 *     </form>
 *     <div data-hs-form-test="info"></div>
 *   </div>
 *
 * Console Output:
 *   [form-test] Submit button clicked
 *   [form-test] Submission prevented - displaying test results
 */

import { querySelectorAll, querySelector } from '@utils';

export function init(config) {
  // Centralized cleanup tracking
  const cleanup = {
    handlers: [],
  };

  const addHandler = (
    element: Element,
    event: string,
    handler: EventListener,
    options?: boolean
  ) => {
    element.addEventListener(event, handler, options);
    cleanup.handlers.push({ element, event, handler, options });
  };

  // Find all test wrappers
  const testWrappers = querySelectorAll(config, 'wrapper');

  testWrappers.forEach((wrapper) => {
    // Find form element within wrapper
    const form = wrapper.querySelector('form') as HTMLFormElement;
    if (!form) {
      console.warn(
        '[form-test] No <form> element found within data-hs-form-test="wrapper"',
        wrapper
      );
      return;
    }

    // Find test info element (optional)
    const testInfo = querySelector(config, 'info', wrapper);

    // Function to collect and display current form data
    const updateLiveData = () => {
      const formData = new FormData(form);
      const data = {};
      for (const [key, value] of formData.entries()) {
        data[key] = value;
      }

      if (testInfo) {
        // Show live form data
        const liveDisplay = document.createElement('pre');
        liveDisplay.textContent = JSON.stringify(data, null, 2);

        testInfo.innerHTML = '';
        testInfo.appendChild(document.createTextNode('Test mode active - Live form data:'));
        testInfo.appendChild(liveDisplay);
      }
    };

    // Set initial message and data
    if (testInfo) {
      testInfo.textContent = 'Test mode active, preventing native submissions';
      // Show initial form state
      updateLiveData();
    }

    // Listen to all form inputs for live updates
    addHandler(form, 'input', updateLiveData);
    addHandler(form, 'change', updateLiveData);

    // Find submit button
    const submitButton =
      form.querySelector('button[type="submit"], input[type="submit"]') ||
      form.querySelector('button:not([type])');

    // Track button clicks to detect when browser validation blocks submission
    if (submitButton) {
      const clickHandler = () => {
        console.log('[form-test] Submit button clicked');

        // Use requestAnimationFrame to check if form will submit
        // If form is invalid, submit event won't fire
        requestAnimationFrame(() => {
          if (!form.checkValidity()) {
            console.log('[form-test] Browser blocked submission - validation failed');
          }
        });
      };
      addHandler(submitButton, 'click', clickHandler);
    }

    // Handle form submission
    const submitHandler = (e: Event) => {
      e.preventDefault(); // Prevent actual submission
      e.stopPropagation(); // Stop event from bubbling
      e.stopImmediatePropagation(); // Stop other listeners on same element

      console.log('[form-test] Submission prevented - displaying test results');

      // Get form data
      const formData = new FormData(form);
      const data = {};
      for (const [key, value] of formData.entries()) {
        data[key] = value;
      }

      // Create success message
      const timestamp = new Date().toLocaleTimeString();
      const message = `✅ Form would submit successfully at ${timestamp}`;

      // Display in test info element or console
      if (testInfo) {
        // Show form data in a formatted way
        const dataDisplay = document.createElement('pre');
        dataDisplay.textContent = JSON.stringify(data, null, 2);

        testInfo.innerHTML = ''; // Clear previous
        testInfo.appendChild(document.createTextNode(message));
        testInfo.appendChild(dataDisplay);
      } else {
        console.log(message);
        console.log('Form data:', data);
      }

      return false; // Extra insurance to prevent submission
    };

    // Use capture phase to catch submit event before anything else
    addHandler(form, 'submit', submitHandler, true);
  });

  return {
    result: `test initialized on ${testWrappers.length} form${testWrappers.length !== 1 ? 's' : ''}`,
    destroy: () => {
      // Remove all event listeners
      cleanup.handlers.forEach(({ element, event, handler, options }) => {
        element.removeEventListener(event, handler, options);
      });
      cleanup.handlers.length = 0;
    },
  };
}
